import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataMap, GetSourceDataReport, Metadata, Participant, SourceData, SourceDataEventContext, SourceDataFieldType, SourceDataFormContext, SourceDataItem, SourceDataPoint, SourceDataReportItem, SourceDataReportType, Trial } from "@/api/dtos";
import { SourceDataItemMapping } from "@/components/forms/forms/source-data/values/Index";
import IsLoading from "@/components/layouts/IsLoading";
import SetLinks from "@/components/layouts/SetLinks";
import Show from "@/components/layouts/Show";
import NotesAndQueriesList from "@/components/lists/notes/NotesAndQueriesList";
import MetadataContext from "@/contexts/data/MetadataContext";
import ParticipantsContext from "@/contexts/data/ParticipantsContext";
import TrialConfigurationSourceDataContext from "@/contexts/data/TrialConfigurationSourceDataContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ReportInstitutionCreateLink, ReportParticipantCreateLink, ReportTrialCreateLink } from "@/contexts/layouts/LinkContext";
import useSourceDataExport, { ISourceDataExportItem } from "@/hooks/data/useSourceDataExport";
import useSourceDataReport from "@/hooks/data/useSourceDataReport";
import useTrialNotesAndQueries from "@/hooks/data/useTrialNotesAndQueries";
import useEventConfiguration from "@/hooks/form/configuration/useEventConfiguration";
import useFormConfiguration from "@/hooks/form/configuration/useFormConfiguration";
import useItemGroupConfiguration from "@/hooks/form/configuration/useItemGroupConfiguration";
import useItem from "@/hooks/form/source-data/useItem";
import { Box, Button, LinearProgress, List, ListItem, ListItemButton, ListItemText, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, styled } from "@mui/material";
import { DateTime } from "luxon";
import { FC, Fragment, forwardRef, useContext, useMemo, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { useNavigate, useSearchParams } from 'react-router-dom';

import { mkConfig, generateCsv, download } from "export-to-csv";
import useSourceDataReportUsers from "@/hooks/data/useSourceDataReportUsers";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import { DateTimePicker } from "@mui/x-date-pickers";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import BasicAlert from "@/components/layouts/BasicAlert";



interface ISourceDataReportItemRow extends ISourceDataExportItem {
    // metadata?: Metadata;
    // trialConfiguration?: ConfigurationSourceDataMap;
    // trial: Trial;
    // participants?: Record<number, Participant>;
    // eventMap?: Record<number, SourceDataEventContext>;
    // formMap?: Record<number, SourceDataFormContext>;
    // pointMap?: Record<number, SourceDataPoint>;
    // item: SourceDataReportItem;
}

const SourceDataReportItemRow: FC<ISourceDataReportItemRow> = ({
    row
}) => {
    //const ValueComponent = SourceDataItemMapping[itemData?.itemMetadata.fieldType ?? SourceDataFieldType.Text];
    
    return (
        <>
            <TableCell>
                {row?.participantStudyNumber}
            </TableCell>
            <TableCell>
                {row?.studyEvent}
            </TableCell>
            <TableCell>
                {row?.caseReportForm}
            </TableCell>
            <TableCell>
                {row.itemGroup}
            </TableCell>
            <TableCell>
                {row?.item}
            </TableCell>
            <TableCell>
                {/* <ValueComponent itemData={itemData} hideBorderStyles={true}  /> */}
                {row?.value}
            </TableCell>
            <TableCell>
                {row?.state}
            </TableCell>
            <TableCell>
                {row?.date}
            </TableCell>
            <TableCell>
                {row?.user}
            </TableCell>
        </>
    )
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const FilterContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center'
}));

const TypeContainer = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(15)
}));

const UserContainer = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(30)
}));

const InstitutionContainer = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(30)
}));

const PatientContainer = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(15)
}));

const DateContainer = styled(Box)(({ theme }) => ({
    minWidth: theme.spacing(30)
}));

const TableComponents = {
    Scroller: forwardRef((props, ref) => <TableContainer component={Paper} {...props} ref={ref as any} />),
    Table: (props: any) => <Table {...props} style={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />,
    TableHead: forwardRef<any, any>(({children, context, ...props}, ref) => (
        <TableHead {...props} ref={ref as any}>
            {children}
            {
                context && (
                    <TableRow>
                        <TableCell colSpan={9} sx={{p: 0}}>
                            <LinearProgress sx={{position: 'relative', zIndex: 100}} /> 
                        </TableCell>
                    </TableRow>
                )
            }
        </TableHead>
    )),
    TableRow: StyledTableRow,
    TableBody: forwardRef<any, any>(({children, context, ...props}, ref) => (
        <TableBody {...props} ref={ref as any}>
            {children}
        </TableBody>
    )),
}

const SourceDataReport: FC = () => {
    const { data: trial } = useContext(TrialContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: participant } = useContext(ParticipantContext);

    const { data: institutions } = useContext(InstitutionsContext);
    const { idMap: participants } = useContext(ParticipantsContext);
    const { data: metadata } = useContext(MetadataContext);
    const { data: trialConfiguration } = useContext(TrialConfigurationSourceDataContext);

    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const searchQuery = useMemo(() => {
        if (!trial?.id) {
            return undefined;
        }

        const output: Partial<GetSourceDataReport> = {
            trialId: trial.id,
            institutionId: institution?.id,
            participantId: participant?.id
        };

        
        if (urlSearchParams.has('username')) {
            output.enteredBy = urlSearchParams.get('username') ?? undefined;
        }

        if (urlSearchParams.has('reportType')) {
            output.type = parseInt(urlSearchParams.get('reportType')!, 10) as SourceDataReportType;
        }
        
        if (urlSearchParams.has('startDate')) {
            output.startDate = urlSearchParams.get('startDate') ?? undefined;
        }
        
        if (urlSearchParams.has('endDate')) {
            output.endDate = urlSearchParams.get('endDate') ?? undefined;
        }

        return output;
    }, [urlSearchParams, trial, institution, participant])
    
    const { data: users, loading: usersLoading, validating: userValidating, usernameMap: userMap } = useSourceDataReportUsers(trial?.id ? { trialId: trial.id } : undefined, { revalidateOnFocus: false });
    const { data: report, loading: reportLoading, validating: reportValidating, eventMap, formMap, pointMap } = useSourceDataReport(searchQuery, { revalidateOnFocus: false });

    const exportData = useSourceDataExport(
        metadata,
        trialConfiguration,
        trial,
        participants,
        eventMap,
        formMap,
        pointMap,
        report?.items
    );

    const loading =  usersLoading;
    const validating = reportLoading || reportValidating || userValidating;

    return (
        <>
            <SetLinks 
                createTrialLink={ReportTrialCreateLink} 
                createInstitutionLink={ReportInstitutionCreateLink} 
                createParticipantLink={ReportParticipantCreateLink}  
            />
            <IsLoading name="trial-notes-page" loading={loading} validating={validating} />
            <Show show={!loading}>
                <Typography variant="h1">
                    Source Data Report
                </Typography>
                <Typography variant="h2" sx={{mb: 2}}>
                    {trial?.name}{institution?.name ? ` - ${institution.name}` : null}{participant?.studyNumber ? ` - ${participant.studyNumber}` : null}
                </Typography>
                <FilterContainer>
                    <TypeContainer>
                        <TextField
                            fullWidth
                            size="small"
                            select 
                            label="Type"
                            value={searchQuery?.type ?? -1} 
                            onChange={(event) => {
                                setUrlSearchParams(currentUrlSearchParams => {                                
                                    if (event.target.value as any !== -1 && event.target.value) {
                                        currentUrlSearchParams.set('reportType', event.target.value.toString());
                                    }
                                    else {
                                        currentUrlSearchParams.delete('reportType')
                                    }

                                    return currentUrlSearchParams;
                                });
                            }}
                        >
                            <MenuItem value={-1}>All</MenuItem>
                            <MenuItem value={SourceDataReportType.Reviewed}>Review</MenuItem>
                            <MenuItem value={SourceDataReportType.Verified}>Verification</MenuItem>
                        </TextField>
                    </TypeContainer>
                    <UserContainer>
                        <TextField
                            fullWidth
                            size="small"
                            select 
                            label="Username"
                            value={searchQuery?.enteredBy ?? 'all'} 
                            onChange={(event) => {
                                setUrlSearchParams(currentUrlSearchParams => {                                
                                    if (event.target.value !== 'all' && event.target.value) {
                                        currentUrlSearchParams.set('username', event.target.value.toString());
                                    }
                                    else {
                                        currentUrlSearchParams.delete('username')
                                    }

                                    return currentUrlSearchParams;
                                });
                            }}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {
                                users?.map(x => {
                                    return (
                                        <MenuItem
                                            key={x.username}
                                            value={x.username}
                                        >
                                            {x.firstName} {x.lastName}
                                        </MenuItem>
                                    )
                                })
                            }
                        </TextField>
                    </UserContainer>
                    <DateContainer>
                        <DateTimePicker
                            label="Start Date"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true
                                }
                            }}
                            format="dd/MM/yyyy hh:mm a"
                            value={searchQuery?.startDate ? DateTime.fromISO(searchQuery.startDate) : null}
                            onChange={(value) => {
                                setUrlSearchParams(currentUrlSearchParams => {                                
                                    if (value) {
                                        currentUrlSearchParams.set('startDate', value.toISO({ includeOffset: false })!);
                                    }
                                    else {
                                        currentUrlSearchParams.delete('startDate')
                                    }

                                    return currentUrlSearchParams;
                                });
                            }}
                        />
                    </DateContainer>
                    <DateContainer>
                        <DateTimePicker
                            label="End Date"
                            
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true
                                }
                            }}
                            format="dd/MM/yyyy hh:mm a"
                            value={searchQuery?.endDate ? DateTime.fromISO(searchQuery.endDate) : null}
                            onChange={(value) => {
                                setUrlSearchParams(currentUrlSearchParams => {                                
                                    if (value) {
                                        currentUrlSearchParams.set('endDate', value.toISO({ includeOffset: false })!);
                                    }
                                    else {
                                        currentUrlSearchParams.delete('endDate')
                                    }

                                    return currentUrlSearchParams;
                                });
                            }}
                        />
                    </DateContainer>
                    <Button
                        sx={{
                            marginLeft: 'auto'
                        }}
                        onClick={() => {

                            let filename = `Source Data`;

                            if (searchQuery?.type) {
                                if (searchQuery.type === SourceDataReportType.Reviewed) {
                                    filename += ' Review'
                                }

                                if (searchQuery.type === SourceDataReportType.Verified) {
                                    filename += ' Verification'
                                }
                            }

                            filename += ` Report - ${trial?.name}`;

                            if (institution) {
                                filename += ` - ${institution?.name}`
                            }

                            if (participant) {
                                filename += ` - ${participant?.studyNumber}`
                            }

                            if (searchQuery?.enteredBy) {
                                filename += ` - ${searchQuery.enteredBy}`
                            }

                            if (searchQuery?.startDate) {
                                filename += ` - from ${searchQuery.startDate}`
                            }

                            if (searchQuery?.endDate) {
                                filename += ` - to ${searchQuery.endDate}`
                            }

                            const csvConfig = mkConfig({ 
                                useKeysAsHeaders: true,
                                filename: filename
                            });
                            
                            const csv = generateCsv(csvConfig)(exportData.map(x => x.row) as any);
                            
                            download(csvConfig)(csv);
                        }}
                    >
                        Download
                    </Button>
                </FilterContainer>
                <Paper>
                    <TableVirtuoso
                        useWindowScroll
                        data={exportData ?? []}
                        components={TableComponents as any}
                        context={reportLoading || reportValidating}
                        fixedHeaderContent={() => (
                            <TableRow>
                                <TableCell
                                    sx={{ width: 100 }}
                                >
                                    Participant
                                </TableCell>
                                <TableCell
                                    sx={{ width: '20%' }}
                                >
                                    Study Event
                                </TableCell>
                                <TableCell
                                    sx={{ width: '20%' }}
                                >
                                    Case Report Form
                                </TableCell>
                                <TableCell
                                    sx={{ width: '20%' }}
                                >
                                    Item Group
                                </TableCell>
                                <TableCell
                                    sx={{ width: '20%' }}
                                >
                                    Item Name
                                </TableCell>
                                <TableCell
                                    sx={{ width: '20%' }}
                                >
                                    Value
                                </TableCell>
                                <TableCell
                                    sx={{ width: 120 }}
                                >
                                    State
                                </TableCell>
                                <TableCell
                                    sx={{ width: 180 }}
                                >
                                    Date
                                </TableCell>
                                <TableCell
                                    sx={{ width: 140 }}
                                >
                                    User
                                </TableCell>
                            </TableRow>
                        )}
                        itemContent={(i, x) => (
                            <SourceDataReportItemRow 
                                key={i}
                                {...x}
                            />
                        )}
                    />

                    { 
                        !reportLoading && !reportValidating && (!report?.items?.length || report.items.length === 0) && (
                            
                            <BasicAlert
                                severity="warning"
                                title="No Data"
                                description={`No data was found for the selected criteria.`}
                            />
                        ) 
                    }
                </Paper>
            </Show>
        </>
    )
};

export default SourceDataReport;